import React from "react";
import moment from "moment";
import { formatDate } from "../../helper/formatDate";

// Shared primitives for the teacher flow, recreated from the spec mockups.

export function useOutside(open, setOpen) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open, setOpen]);
  return ref;
}

// Ensure the Material Symbols font the design uses is loaded once.
export function useMaterialSymbols() {
  React.useEffect(() => {
    const id = "oir-material-symbols";
    if (document.getElementById(id)) return;
    const l = document.createElement("link");
    l.id = id;
    l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=block";
    document.head.appendChild(l);
  }, []);
}

export const Msr = ({ children, style }) => <span className="msr" style={style}>{children}</span>;
export const Sep = () => <span className="casc-sep"><Msr>chevron_right</Msr></span>;

// Should a popover under `el` open upward? True when the space below the
// trigger (less the sticky action bar) can't fit the menu but above can —
// on phones a dropdown tapped near the bottom otherwise renders under the
// fold/action bar and reads as broken.
export function popUp(el, menuMax = 270, barAllowance = 70) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  const below = window.innerHeight - r.bottom - barAllowance;
  return below < menuMax && r.top > below;
}

// Generic cascade dropdown: options = [{value,label,meta?}]
export function Drop({ value, placeholder, options, onChange, onClear, clearLabel, reqEmpty, width }) {
  const [open, setOpen] = React.useState(false);
  const [up, setUp] = React.useState(false);
  const ref = useOutside(open, setOpen);
  const cur = options.find((o) => o.value === value);
  return (
    <div className="dd" ref={ref}>
      <button type="button" className={"dd-btn" + (open ? " open" : "") + (value ? "" : reqEmpty ? " empty req-empty" : " empty")}
        style={width ? { width } : null}
        onClick={() => setOpen((o) => { if (!o) setUp(popUp(ref.current)); return !o; })}>
        <span className="tx">{cur ? cur.label : placeholder}</span>
        <Msr>expand_more</Msr>
      </button>
      {open && (
        <div className={"dd-pop" + (up ? " up" : "")}>
          {options.map((o) => (
            <div key={o.value} className={"dd-opt" + (value === o.value ? " sel" : "")}
              onClick={() => { onChange(o.value); setOpen(false); }}>
              <span>{o.label}</span>
              {o.meta && <span className="meta">{o.meta}</span>}
              {value === o.value && <span className="msr ck-i">check</span>}
            </div>
          ))}
          {onClear && value && (
            <div className="dd-clear" onClick={() => { onClear(); setOpen(false); }}>
              <Msr>close</Msr>{clearLabel || "Clear selection"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The registration steps are DERIVED per event (screens.md Part 1): the
// Assessment step exists only when the event charges a school assessment
// (acceptance D1 — no assessment, no reclaim step).
export const stepsFor = (event) => [
  { key: "solo", label: "Solo entries" },
  { key: "teams", label: "Teams" }, // M4
  ...(event && event.fees && event.fees.schoolAssessmentPerDancer
    ? [{ key: "assessment", label: "Assessment" }] // M5
    : []),
  { key: "checkout", label: "Checkout" },
];

export function RegSteps({ steps = [], current, onGo }) {
  const ci = steps.findIndex((s) => s.key === current);
  return (
    <div className="rsteps">
      {steps.map((s, i) => {
        const state = i < ci ? "done" : i === ci ? "current" : "upcoming";
        const clickable = state !== "current";
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <span className={"rs-line" + (i <= ci ? " done" : "")}></span>}
            <button type="button" className={"rstep " + state + (clickable ? " clickable" : "")}
              onClick={() => clickable && onGo(s.key)}>
              <span className="rs-dot">{state === "done" ? <Msr>check</Msr> : i + 1}</span>
              <span className="rs-label">{s.label}</span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function ActionBar({ back, next, status }) {
  return (
    <div className="actionbar">
      <div className="actionbar-in">
        {back ? (
          <button type="button" className="ab-back" onClick={back.onClick}>
            <Msr>arrow_back</Msr>{back.label}
          </button>
        ) : <span />}
        <div className="ab-status">{status}</div>
        {next ? (
          <button type="button" className="ab-next" onClick={next.onClick} disabled={next.disabled}>
            {next.label}<Msr>arrow_forward</Msr>
          </button>
        ) : <span />}
      </div>
    </div>
  );
}

export function PageHeader({ event, school }) {
  // formatDate strips the UTC offset (repo-wide pattern) so the deadline
  // doesn't render a day early west of UTC.
  const close = event && event.entriesCloseDate
    ? moment(formatDate(event.entriesCloseDate)).format("MMM D")
    : null;
  return (
    <div className="ph">
      {event && event.logo && <img className="logo" src={event.logo} alt="" />}
      <div>
        <h1>{event ? `${event.name} — Register dancers` : "Register dancers"}</h1>
        <div className="sub">
          {school && <span className="sch"><Msr>school</Msr>{school}</span>}
          {close && <span className="dl"><Msr>schedule</Msr>Entries close {close}</span>}
        </div>
      </div>
    </div>
  );
}
